import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowLeft, Mail, Check } from 'lucide-react';
import { Logo, LogoWhite } from '@/components/Logo';
import { Seo } from '@/components/Seo';
import api from '@/lib/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      let target = '/dashboard';
      try {
        const raw = localStorage.getItem('seatPreset');
        if (raw) {
          const { seats, cycle } = JSON.parse(raw);
          if (seats) target = `/billing?seats=${seats}&cycle=${cycle || 'monthly'}`;
        }
      } catch { /* ignore parse errors */ }
      navigate(target);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      let target = '/dashboard';
      try {
        const raw = localStorage.getItem('seatPreset');
        if (raw) {
          const { seats, cycle } = JSON.parse(raw);
          if (seats) target = `/billing?seats=${seats}&cycle=${cycle || 'monthly'}`;
        }
      } catch { /* ignore */ }
      navigate(target);
    }
    else setError(result.error);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { setForgotError('Please enter your email'); return; }
    setForgotError('');
    setForgotLoading(true);
    try {
      await api.forgotPassword(forgotEmail.trim());
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Something went wrong');
    }
    setForgotLoading(false);
  };

  const backToLogin = () => {
    setShowForgot(false);
    setForgotSent(false);
    setForgotEmail('');
    setForgotError('');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="login-page">
      <Seo
        title="Sign In — Real Leads CRM"
        description="Sign in to your Real Leads CRM account. Shared lead pipeline, AI voice notes, and team performance dashboards."
        path="/login"
      />
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_crm-collab-1/artifacts/mw2milqi_customer.jpg"
          alt="Professional using Real Leads CRM"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0047AB]/70" />
        <div className="relative flex items-end p-12 w-full">
          <div className="max-w-md">
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-3">Welcome back to Real Leads CRM</h2>
            <p className="text-blue-200 text-sm leading-relaxed">Sign in to access your leads, collaborate with your team, and close more deals together.</p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex flex-col items-center">
            <img src="https://customer-assets.emergentagent.com/job_crm-collab-1/artifacts/tbnvb496_Logo.png" alt="Real Leads CRM" className="w-36 h-36 object-contain" />
          </div>
          <div className="hidden lg:flex justify-center mb-6">
            <img src="https://customer-assets.emergentagent.com/job_crm-collab-1/artifacts/tbnvb496_Logo.png" alt="Real Leads CRM" className="w-44 h-44 object-contain" data-testid="login-encore-logo" />
          </div>

          {!showForgot ? (
            <>
              <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-1">Sign in</h1>
              <p className="text-sm text-slate-500 mb-6">Enter your credentials to access your account</p>
              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs font-medium text-slate-700 uppercase tracking-wide">Email</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1 rounded-sm" required data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-xs font-medium text-slate-700 uppercase tracking-wide">Password</Label>
                    <button
                      type="button"
                      onClick={() => { setShowForgot(true); setForgotEmail(email); }}
                      className="text-xs text-[#0047AB] font-medium hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="mt-1 rounded-sm" required data-testid="login-password-input" />
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10 text-sm font-medium" disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <p className="text-sm text-slate-500 text-center mt-6">
                Don't have an account? <Link to="/register" className="text-[#0047AB] font-medium hover:underline" data-testid="login-register-link">Create one</Link>
              </p>
            </>
          ) : (
            <>
              <button onClick={backToLogin} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </button>

              {!forgotSent ? (
                <>
                  <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-1">Reset password</h1>
                  <p className="text-sm text-slate-500 mb-6">Enter your email and we'll send you a link to reset your password.</p>
                  {forgotError && (
                    <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" data-testid="forgot-error">
                      <AlertCircle className="w-4 h-4 shrink-0" /> {forgotError}
                    </div>
                  )}
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <Label htmlFor="forgot-email" className="text-xs font-medium text-slate-700 uppercase tracking-wide">Email Address</Label>
                      <Input id="forgot-email" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="you@company.com" className="mt-1 rounded-sm" required data-testid="forgot-email-input" />
                    </div>
                    <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10 text-sm font-medium" disabled={forgotLoading} data-testid="forgot-submit-btn">
                      {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </form>
                </>
              ) : (
                <div className="text-center" data-testid="forgot-success">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-2">Check your email</h1>
                  <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                    We've sent a password reset link to <strong className="text-slate-700">{forgotEmail}</strong>. The link expires in 15 minutes.
                  </p>
                  <p className="text-xs text-slate-400">Didn't receive it? Check your spam folder or <button onClick={() => setForgotSent(false)} className="text-[#0047AB] hover:underline">try again</button>.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
